/**
 * Distilleries page component
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Pagination, ConfirmDialog } from '../components/common';
import {
  DistilleryList,
  DistilleryForm,
  DistilleryFilters
} from '../components/distilleries';
import {
  useDistilleries,
  useCreateDistillery,
  useUpdateDistillery,
  useDeleteDistillery
} from '../hooks/useDistilleries';
import { useToast } from '../context/ToastContext';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../utils/constants';
import './Distilleries.css';

/**
 * Distilleries management page with CRUD operations
 * @returns {React.ReactElement} Page element
 */
export default function Distilleries() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;
  const sortBy = searchParams.get('sort') || 'distillery_name';
  const sortOrder = searchParams.get('order') || 'asc';
  const filters = useMemo(() => {
    const f = {};
    const search = searchParams.get('search');
    const country = searchParams.get('country');
    if (search) f.search = search;
    if (country) f.country = country;
    return f;
  }, [searchParams]);

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedDistillery, setSelectedDistillery] = useState(null);
  const [distilleryToDelete, setDistilleryToDelete] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const hasActiveFilters = Object.keys(filters).some(k => k !== 'search');

  const { data, isLoading } = useDistilleries({
    page,
    limit: pageSize,
    sortBy,
    sortOrder,
    filters
  });

  const createDistillery = useCreateDistillery();
  const updateDistillery = useUpdateDistillery();
  const deleteDistillery = useDeleteDistillery();

  const handleSort = (column, direction) => {
    updateParams({
      sort: column === 'distillery_name' ? '' : column,
      order: direction === 'asc' ? '' : direction,
    });
  };

  const handleFilterChange = (newFilters) => {
    updateParams({
      search: newFilters.search || '',
      country: newFilters.country || '',
      page: '',
    });
  };

  const handlePageSizeChange = (size) => {
    updateParams({
      pageSize: size === DEFAULT_PAGE_SIZE ? '' : size,
      page: '',
    });
  };

  const handlePageChange = (newPage) => {
    updateParams({ page: newPage === 1 ? '' : newPage });
  };

  const handleAddClick = () => {
    setSelectedDistillery(null);
    setShowFormModal(true);
  };

  const handleEditClick = (distillery) => {
    setSelectedDistillery(distillery);
    setShowFormModal(true);
  };

  const handleDeleteClick = (distillery) => {
    setDistilleryToDelete(distillery);
  };

  const handleViewClick = (distillery) => {
    navigate(`/distilleries/${distillery.id}`);
  };

  const handleViewBottles = (distillery) => {
    navigate(`/bottles?distillery=${distillery.id}`);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (selectedDistillery) {
        await updateDistillery.mutateAsync({
          id: selectedDistillery.id,
          updates: formData
        });
        addToast({ type: 'success', message: 'Distillery updated successfully' });
      } else {
        await createDistillery.mutateAsync(formData);
        addToast({ type: 'success', message: 'Distillery created successfully' });
      }
      setShowFormModal(false);
      setSelectedDistillery(null);
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to save distillery'
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDistillery.mutateAsync(distilleryToDelete.id);
      addToast({ type: 'success', message: 'Distillery deleted successfully' });
      setDistilleryToDelete(null);
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to delete distillery'
      });
    }
  };

  const isFormLoading = createDistillery.isPending || updateDistillery.isPending;

  return (
    <div className="distilleries-page">
      <div className="distilleries-page__header">
        <h1 className="distilleries-page__title">Distilleries</h1>
        <div className="distilleries-page__actions">
          <button
            className={`icon-btn icon-btn--filter${filtersOpen ? ' icon-btn--active' : ''}`}
            onClick={() => setFiltersOpen(prev => !prev)}
            aria-label="Toggle filters"
            title="Toggle filters"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {hasActiveFilters && !filtersOpen && <span className="icon-btn__badge" />}
          </button>
          <button
            className="icon-btn icon-btn--add"
            onClick={handleAddClick}
            aria-label="Add Distillery"
            title="Add Distillery"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      <DistilleryFilters filters={filters} onFilterChange={handleFilterChange} showAdvanced={filtersOpen} />

      <DistilleryList
        distilleries={data?.data || []}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onSelect={handleViewClick}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        onViewBottles={handleViewBottles}
        loading={isLoading}
      />

      <Pagination
        currentPage={page}
        totalItems={data?.count || 0}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />

      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={selectedDistillery ? 'Edit Distillery' : 'Add Distillery'}
        size="lg"
      >
        <DistilleryForm
          distillery={selectedDistillery}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowFormModal(false)}
          loading={isFormLoading}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!distilleryToDelete}
        onClose={() => setDistilleryToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Distillery"
        message={`Are you sure you want to delete "${distilleryToDelete?.distillery_name}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteDistillery.isPending}
      />
    </div>
  );
}
